import React, { useState, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import UserBackupManager from './UserBackupManager';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Database, 
  Download, 
  Upload, 
  AlertCircle,
  FileDown,
  FileUp
} from 'lucide-react';

const SimpleBackupPage = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const handleBackup = async () => {
    try {
      setLoading(true);
      toast.info('Création de la sauvegarde...');
      
      // Simulation sauvegarde
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const backupData = {
        timestamp: new Date().toISOString(),
        user: user.username,
        status: 'completed',
        data: 'Données de sauvegarde simulées'
      };
      
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `boulangerie_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Sauvegarde créée et téléchargée !');
      
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      if (!window.confirm('ATTENTION: Cette action supprimera toutes les données actuelles. Continuer ?')) {
        return;
      }
      
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          setRestoreLoading(true);
          
          try {
            const reader = new FileReader();
            reader.onload = async (e) => {
              try {
                const data = JSON.parse(e.target.result);
                
                toast.info('Restauration en cours...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                toast.success('Restauration terminée !');
                setTimeout(() => window.location.reload(), 2000);
                
              } catch (error) {
                toast.error('Fichier invalide');
                setRestoreLoading(false);
              }
            };
            reader.readAsText(file);
          } catch (error) {
            toast.error('Erreur de restauration');
            setRestoreLoading(false);
          }
        }
      };
      input.click();
      
    } catch (error) {
      console.error('Restore error:', error);
      toast.error('Erreur lors de la restauration');
      setRestoreLoading(false);
    }
  };

  if (!user || user.role !== 'system_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès Refusé</h2>
            <p className="text-gray-600">Seul l'administrateur système peut accéder aux sauvegardes.</p>
            <p className="text-sm text-gray-500 mt-2">Connectez-vous avec le compte administrateur.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Sauvegarde & Restauration du Système
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sauvegarde Système */}
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-blue-600" />
                Sauvegarde Système Générale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Données sauvegardées :</p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Productions, ventes, retours</li>
                    <li>• Stock et dépenses</li>
                    <li>• Photos et documents</li>
                    <li>• Configuration générale</li>
                  </ul>
                </div>

                <Button
                  onClick={handleBackup}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="backup-system-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <FileDown className="h-4 w-4 mr-2" />
                      Créer Sauvegarde Système
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Restauration */}
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-red-600" />
                Restaurer le Système
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">⚠️ ATTENTION :</p>
                  <ul className="text-xs text-red-700 space-y-1">
                    <li>• Supprime toutes les données actuelles</li>
                    <li>• Action irréversible</li>
                    <li>• Redémarrage du système</li>
                  </ul>
                </div>

                <Button
                  onClick={handleRestore}
                  disabled={restoreLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="restore-system-btn"
                >
                  {restoreLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Restauration...
                    </>
                  ) : (
                    <>
                      <FileUp className="h-4 w-4 mr-2" />
                      Restaurer le Système
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gestion des Utilisateurs */}
        <div className="mt-8">
          <UserBackupManager />
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SimpleBackupPage;